import React from 'react';
import { IonIcon, IonSkeletonText } from '@ionic/react';
import { schoolOutline, openOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useKindergartenForBetelFamily } from '@/hooks/useKindergarten';
import { usePermission } from '@/hooks/usePermission';
import { KG_ENROLLMENT_STATUS_LABELS, KG_FEE_RULE_LABELS } from '@/utils/labels';
import type { KgChild } from '@/types/kindergarten';

interface Props {
  /** The CRM family id (families table), not the kindergarten household id. */
  familyId: string;
}

/**
 * On a registry family page: which of this family's children attend the
 * kindergarten this year, and in which group. Shown to anyone holding
 * `kindergarten.view` (the pastor by default); money is never shown here.
 */
const FamilyKindergartenCard: React.FC<Props> = ({ familyId }) => {
  const history = useHistory();
  const { can } = usePermission();
  const canOpenModule = can('kindergarten.manage') || can('kindergarten.finance_view');
  const { data: households, isLoading } = useKindergartenForBetelFamily(familyId);

  const children: Array<{ householdId: string; child: KgChild }> = (households ?? []).flatMap((h) =>
    (h.children ?? []).map((child) => ({ householdId: h.id, child })),
  );
  const firstHousehold = households?.[0];

  // Nothing linked → nothing to show; the card stays out of the way.
  if (!isLoading && children.length === 0) return null;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 24px',
      marginBottom: 20, border: '1px solid #ececf2',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IonIcon icon={schoolOutline} style={{ fontSize: 20, color: '#5e81f4' }} />
          <h3 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#1c1d21', margin: 0 }}>
            Copii la Grădinița Gosen
          </h3>
        </div>
        {canOpenModule && firstHousehold && (
          <button
            type="button"
            onClick={() => history.push(`/kindergarten/families/${firstHousehold.id}`)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 8, border: '1px solid #ececf2', background: '#fff',
              fontSize: 12, fontWeight: 600, color: '#5e81f4', cursor: 'pointer',
            }}
          >
            Deschide în grădiniță <IonIcon icon={openOutline} />
          </button>
        )}
      </div>

      {isLoading ? (
        <IonSkeletonText animated style={{ width: '60%', height: 18 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {children.map(({ child }) => {
            const enrollment = child.enrollments?.[0];
            return (
              <div
                key={child.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: '10px 12px', borderRadius: 10, background: '#f8f9fc',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1c1d21' }}>{child.full_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {child.age !== null ? `${child.age} ani` : 'vârstă necunoscută'}
                    {enrollment?.group ? ` · ${enrollment.group.display_name}` : ' · neînscris anul acesta'}
                    {enrollment?.group?.teacher_name ? ` · ${enrollment.group.teacher_name}` : ''}
                  </div>
                </div>
                {enrollment && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                      background: enrollment.status === 'active' ? '#ecfdf5' : '#f3f4f6',
                      color: enrollment.status === 'active' ? '#047857' : '#6b7280',
                    }}>
                      {KG_ENROLLMENT_STATUS_LABELS[enrollment.status]}
                    </span>
                    {enrollment.fee_rule === 'church_covered' && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                        background: '#eef2ff', color: '#4338ca',
                      }}>
                        {KG_FEE_RULE_LABELS.church_covered}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FamilyKindergartenCard;
